import { usePersistedState } from '../use-persisted-state';
import { Panel } from '../whoop-panel';
import { RecoveryRing, Spark, gen } from '../whoop-charts';
import { GOAL } from '../whoop-goal';
import { C } from '../whoop-theme';

const REASONS = [
  'Recovery is 72% — below your recent quality-day average of 81%.',
  'HRV is 58 ms, about 8% under your 30-day baseline.',
  'Resting HR is 53 bpm, 4 bpm above baseline.',
  'You have a 16-mile long run scheduled in 3 days.',
];

const METRICS = [
  {
    label: 'HRV',
    value: '58 ms',
    delta: '8% below 30-day',
    deltaColor: C.warn,
    series: gen(20, 62, 4, -0.25),
    sparkColor: C.accent,
    baseline: 63,
  },
  {
    label: 'Resting HR',
    value: '53 bpm',
    delta: '+4 vs baseline',
    deltaColor: C.warn,
    series: gen(20, 49, 2, 0.18),
    sparkColor: C.low,
    baseline: 49,
  },
  {
    label: 'Sleep',
    value: '6h 42m',
    delta: '52 min below target',
    deltaColor: C.warn,
    series: gen(20, 7.1, 0.6, -0.04),
    sparkColor: C.accent,
    baseline: 7.5,
  },
  {
    label: 'Yesterday strain',
    value: '15.1',
    delta: 'above usual hard day',
    deltaColor: C.text2,
    series: gen(20, 12, 3, 0.05),
    sparkColor: C.good,
    baseline: null,
  },
];

const PLANNED = {
  type: 'Threshold session',
  duration: '~55 min',
  strain: 'Mod–high',
  steps: [
    { n: '1', text: '15 min easy warm-up' },
    { n: '2', text: '3 × 8 min @ threshold (7:35/mi)' },
    { n: '3', text: '2 min easy jog between reps' },
    { n: '4', text: '10 min cool-down' },
  ],
};

const ADJUSTED = {
  type: 'Threshold session — trimmed',
  duration: '~42 min',
  strain: 'Moderate',
  steps: [
    { n: '1', text: '15 min easy warm-up' },
    { n: '2', text: '2 × 8 min @ threshold (7:35/mi)' },
    { n: '3', text: '2 min easy jog between reps' },
    { n: '4', text: '10 min cool-down' },
  ],
  reason:
    'recovery is acceptable but below your quality-day range. Two reps preserve the workout’s purpose without stacking fatigue before the long run.',
};

const SIGNALS = [
  {
    label: 'Recovery',
    tag: 'below normal',
    color: C.warn,
    detail: '72% vs. 81% recent quality-day average.',
  },
  { label: 'HRV', tag: '−8%', color: C.warn, detail: '58 ms against a 63 ms 14-day baseline.' },
  {
    label: 'Resting HR',
    tag: 'elevated',
    color: C.warn,
    detail: '53 bpm, 4 bpm above your baseline.',
  },
  {
    label: 'Sleep',
    tag: 'short',
    color: C.warn,
    detail: '6h 42m, 52 min below your 7h 34m target.',
  },
  {
    label: 'Recent strain',
    tag: 'high',
    color: C.text2,
    detail: 'Yesterday’s 15.1 was above your usual hard-day load.',
  },
  {
    label: 'Upcoming load',
    tag: 'long run in 3d',
    color: C.accent,
    detail: '16-mile long run Saturday needs protected legs.',
  },
];

const TOMORROW = [
  {
    tag: 'If you do the adjusted workout today',
    tagColor: C.good,
    border: 'rgba(127,209,163,0.25)',
    text: 'Tomorrow stays as planned — an easy 40 min aerobic run. Recovery should rebound into the 80s.',
  },
  {
    tag: 'If you do the full original workout',
    tagColor: C.warn,
    border: 'rgba(227,198,115,0.25)',
    text: 'Tomorrow likely converts to a recovery day, and Saturday’s long run may need to shorten by 2–3 miles.',
  },
];

const ACTIONS: { key: string; label: string }[] = [
  { key: 'adjust', label: 'Use adjusted workout' },
  { key: 'keep', label: 'Keep original' },
  { key: 'tomorrow', label: 'Move to tomorrow' },
  { key: 'swap', label: 'Swap with another day' },
  { key: 'rest', label: 'Mark as rest' },
];

const CHOSEN_MSG: Record<string, string> = {
  adjust: 'Adjusted workout added to today — 2 × 8 min threshold.',
  keep: 'Original workout kept — 3 × 8 min threshold.',
  tomorrow: 'Workout moved to tomorrow. Today is now an easy 30 min shake-out.',
  swap: 'Opened the calendar to pick a day to swap with.',
  rest: 'Today marked as a recovery day. Mobility + 20 min walk suggested.',
};

export function NowPage() {
  const [chosen, setChosen] = usePersistedState<string | null>('nowChosen', null);
  const [whyOpen, setWhyOpen] = usePersistedState<boolean>('whyOpen', true);
  const chosenMsg = chosen ? (CHOSEN_MSG[chosen] ?? null) : null;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 14,
          marginBottom: 26,
        }}
      >
        <div>
          <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.025em', margin: 0 }}>
            Today
          </h1>
          <div style={{ fontSize: 14, color: C.text2, marginTop: 4 }}>
            Wednesday, June 29 · marathon build, {GOAL.phase} phase
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 12,
            color: C.text3,
            fontFamily: "'JetBrains Mono',monospace",
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 8,
            padding: '7px 11px',
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.good }} />
          WHOOP synced 8 min ago
        </div>
      </div>

      {/* Daily decision card */}
      <div
        style={{
          border: '1px solid rgba(181,155,255,0.32)',
          background: 'linear-gradient(180deg,rgba(181,155,255,0.09),rgba(181,155,255,0.03))',
          borderRadius: 18,
          padding: '26px 28px',
          marginBottom: 22,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 16,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: 10,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: C.accent,
              border: '1px solid rgba(181,155,255,0.4)',
              borderRadius: 6,
              padding: '4px 9px',
            }}
          >
            Today&apos;s recommendation
          </span>
          <span
            style={{
              fontSize: '12.5px',
              fontWeight: 600,
              color: '#cbc9c2',
              background: 'rgba(255,255,255,0.06)',
              borderRadius: 6,
              padding: '4px 10px',
            }}
          >
            Proceed with reduced volume
          </span>
          <span
            style={{
              fontSize: 12,
              color: C.text2,
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            Confidence<span style={{ fontWeight: 600, color: C.text }}>Moderate</span>
          </span>
        </div>
        <div
          style={{
            fontSize: 24,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            lineHeight: 1.28,
            maxWidth: 760,
            marginBottom: 18,
          }}
        >
          Keep your planned threshold session, but cut it from 3 × 8 min to 2 × 8 min.
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '8px 28px',
            maxWidth: 760,
            marginBottom: 20,
          }}
        >
          {REASONS.map((r) => (
            <div
              key={r}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 9,
                fontSize: '13.5px',
                color: '#cbc9c2',
                lineHeight: 1.45,
              }}
            >
              <span style={{ color: C.accent, marginTop: 1 }}>—</span>
              {r}
            </div>
          ))}
        </div>
        <div
          style={{
            fontSize: '13.5px',
            color: C.text2,
            lineHeight: 1.5,
            maxWidth: 760,
            padding: '13px 16px',
            background: 'rgba(255,255,255,0.035)',
            borderRadius: 10,
            marginBottom: 20,
          }}
        >
          <span style={{ color: C.text, fontWeight: 600 }}>Expected impact — </span>
          You keep the threshold stimulus that moves marathon pace, while shedding the third rep
          that would mostly add fatigue ahead of Saturday’s long run.
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9 }}>
          {ACTIONS.map((action) => {
            const isChosen = chosen === action.key;
            const isSuggested = chosen == null && action.key === 'adjust';
            const highlighted = isChosen || isSuggested;
            return (
              <button
                key={action.key}
                type="button"
                onClick={() => setChosen(isChosen ? null : action.key)}
                className="ww-btn"
                style={{
                  fontFamily: 'inherit',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  borderRadius: 9,
                  padding: '10px 15px',
                  border: `1px solid ${highlighted ? C.accent : 'rgba(255,255,255,0.12)'}`,
                  background: highlighted ? C.accent : 'rgba(255,255,255,0.05)',
                  color: highlighted ? '#1a1330' : chosen != null ? C.text2 : C.text,
                }}
              >
                {action.label}
              </button>
            );
          })}
        </div>
        {chosenMsg && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginTop: 16,
              padding: '11px 14px',
              background: 'rgba(127,209,163,0.1)',
              border: '1px solid rgba(127,209,163,0.3)',
              borderRadius: 10,
              fontSize: 13,
              color: '#a9e3c4',
            }}
          >
            <span style={{ fontWeight: 700 }}>✓</span>
            <span style={{ flex: 1 }}>{chosenMsg}</span>
            <span
              onClick={() => setChosen(null)}
              style={{
                cursor: 'pointer',
                color: C.text2,
                textDecoration: 'underline',
                fontSize: 12,
              }}
            >
              undo
            </span>
          </div>
        )}
      </div>

      {/* Readiness + workouts */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '336px 1fr',
          gap: 18,
          marginBottom: 22,
          alignItems: 'start',
        }}
      >
        <Panel style={{ padding: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.02em', marginBottom: 18 }}>
            Daily readiness
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
            <RecoveryRing pct={72} color={C.warn} />
          </div>
          <div
            style={{
              textAlign: 'center',
              fontSize: '12.5px',
              color: C.text2,
              marginBottom: 20,
              lineHeight: 1.4,
            }}
          >
            Acceptable, but below your normal quality-workout range
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {METRICS.map((m) => (
              <div
                key={m.label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '11px 4px',
                  borderTop: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{m.label}</div>
                  <div
                    style={{
                      fontSize: '11.5px',
                      color: m.deltaColor,
                      fontFamily: "'JetBrains Mono',monospace",
                      marginTop: 2,
                    }}
                  >
                    {m.delta}
                  </div>
                </div>
                <div style={{ flex: 'none' }}>
                  <Spark series={m.series} color={m.sparkColor} baseline={m.baseline} />
                </div>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    fontFamily: "'JetBrains Mono',monospace",
                    minWidth: 62,
                    textAlign: 'right',
                  }}
                >
                  {m.value}
                </div>
              </div>
            ))}
          </div>
        </Panel>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Panel style={{ padding: 22 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 14,
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: 10,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: C.text3,
                  }}
                >
                  Planned · original
                </div>
                <div style={{ fontSize: 17, fontWeight: 700, marginTop: 4 }}>{PLANNED.type}</div>
              </div>
              <div
                style={{
                  textAlign: 'right',
                  fontSize: '11.5px',
                  color: C.text2,
                  fontFamily: "'JetBrains Mono',monospace",
                }}
              >
                {PLANNED.duration}
                <br />
                {PLANNED.strain}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {PLANNED.steps.map((s) => (
                <div
                  key={s.n}
                  style={{
                    display: 'flex',
                    gap: 11,
                    fontSize: '13.5px',
                    color: '#cbc9c2',
                    alignItems: 'baseline',
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono',monospace",
                      fontSize: 11,
                      color: C.text3,
                      minWidth: 14,
                    }}
                  >
                    {s.n}
                  </span>
                  {s.text}
                </div>
              ))}
            </div>
          </Panel>

          <Panel accent style={{ padding: 22 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 14,
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: 10,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: C.accent,
                  }}
                >
                  Recommended · adjusted
                </div>
                <div style={{ fontSize: 17, fontWeight: 700, marginTop: 4 }}>{ADJUSTED.type}</div>
              </div>
              <div
                style={{
                  textAlign: 'right',
                  fontSize: '11.5px',
                  color: C.text2,
                  fontFamily: "'JetBrains Mono',monospace",
                }}
              >
                {ADJUSTED.duration}
                <br />
                {ADJUSTED.strain}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 16 }}>
              {ADJUSTED.steps.map((s) => (
                <div
                  key={s.n}
                  style={{
                    display: 'flex',
                    gap: 11,
                    fontSize: '13.5px',
                    color: C.text,
                    alignItems: 'baseline',
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono',monospace",
                      fontSize: 11,
                      color: C.accent,
                      minWidth: 14,
                    }}
                  >
                    {s.n}
                  </span>
                  {s.text}
                </div>
              ))}
            </div>
            <div
              style={{
                fontSize: 13,
                color: C.text2,
                lineHeight: 1.5,
                paddingTop: 14,
                borderTop: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <span style={{ color: '#cbc9c2', fontWeight: 600 }}>Why this change — </span>
              {ADJUSTED.reason}
            </div>
          </Panel>
        </div>
      </div>

      {/* Why */}
      <Panel style={{ padding: 22, marginBottom: 22 }}>
        <div
          onClick={() => setWhyOpen(!whyOpen)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 700 }}>Why this recommendation exists</div>
          <span style={{ fontSize: 12, color: C.text2, fontFamily: "'JetBrains Mono',monospace" }}>
            {whyOpen ? 'hide signals ▴' : 'show signals ▾'}
          </span>
        </div>
        {whyOpen && (
          <>
            <div
              style={{
                marginTop: 18,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill,minmax(232px,1fr))',
                gap: 12,
              }}
            >
              {SIGNALS.map((sig) => (
                <div
                  key={sig.label}
                  style={{
                    border: '1px solid rgba(255,255,255,0.07)',
                    borderRadius: 11,
                    padding: '13px 15px',
                    background: 'rgba(255,255,255,0.02)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 5,
                    }}
                  >
                    <span style={{ fontSize: '12.5px', fontWeight: 600 }}>{sig.label}</span>
                    <span
                      style={{
                        fontSize: 11,
                        fontFamily: "'JetBrains Mono',monospace",
                        color: sig.color,
                      }}
                    >
                      {sig.tag}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: C.text2, lineHeight: 1.4 }}>{sig.detail}</div>
                </div>
              ))}
            </div>
            <div
              style={{
                marginTop: 16,
                fontSize: '12.5px',
                color: C.text3,
                lineHeight: 1.5,
                fontStyle: 'italic',
              }}
            >
              WHOOP is one input, not the whole truth — your training consistency is strong this
              block, which is why a reduced session is preferred over full rest.
            </div>
          </>
        )}
      </Panel>

      {/* Tomorrow */}
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>
          Tomorrow, depending on today
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {TOMORROW.map((t) => (
            <div
              key={t.tag}
              style={{
                border: `1px solid ${t.border}`,
                background: C.panel,
                borderRadius: 14,
                padding: '18px 20px',
              }}
            >
              <div
                style={{ fontSize: '12.5px', fontWeight: 600, color: t.tagColor, marginBottom: 8 }}
              >
                {t.tag}
              </div>
              <div style={{ fontSize: 14, color: '#cbc9c2', lineHeight: 1.5 }}>{t.text}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
